'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/hen-ui/utils';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Where menu content sits on larger screens. `divided` is the side column
 * behind a line; `inline` is the next column after the link columns.
 */
export type MenuContentPlacement = 'divided' | 'inline';

export interface MenuContentSlotValue {
  /** Label of the top-level menu whose panel hosts the slot. */
  menu: string;
  /** Counts matching content while it is mounted. Returns the unregister. */
  register: (placement: MenuContentPlacement) => () => void;
  /** Whether inline content takes the menu content width cap. */
  capsInline?: boolean;
}

const MenuContentSlotContext = createContext<MenuContentSlotValue | null>(null);

const normalizeMenuName = (name: string | undefined): string =>
  name?.trim().toLowerCase() ?? '';

/**
 * Whether content tagged for `menu` belongs in the enclosing menu panel.
 * Names compare trimmed and case-insensitive; a blank `menu` belongs in
 * every panel. Returns `null` outside a menu panel.
 */
export function useMenuContentMatch(
  menu: string | undefined,
  placement: MenuContentPlacement = 'divided',
): boolean | null {
  const slot = useContext(MenuContentSlotContext);
  const target = normalizeMenuName(menu);
  const matches = slot
    ? target === '' || target === normalizeMenuName(slot.menu)
    : null;
  const register = slot?.register;

  useIsomorphicLayoutEffect(() => {
    if (!matches || !register) return undefined;
    return register(placement);
  }, [matches, register, placement]);

  return matches;
}

/**
 * Whether the enclosing slot caps inline content at
 * `--cds-navbar-menu-content-width`. Only the desktop panel does; the
 * small-screen drawer leaves inline content full width.
 */
export function useMenuContentCapsInline(): boolean {
  return useContext(MenuContentSlotContext)?.capsInline === true;
}

/**
 * State for one menu panel's content slot. `hasContent` turns true once
 * content that matches `menu` mounts inside the slot, before paint.
 * `placement` is `inline` only when every matching block asks for it.
 */
export function useMenuContentSlot(menu: string) {
  const [counts, setCounts] = useState({ divided: 0, inline: 0 });
  const register = useCallback((placement: MenuContentPlacement) => {
    setCounts((current) => ({
      ...current,
      [placement]: current[placement] + 1,
    }));
    return () =>
      setCounts((current) => ({
        ...current,
        [placement]: current[placement] - 1,
      }));
  }, []);
  const value = useMemo(() => ({ menu, register }), [menu, register]);
  const placement: MenuContentPlacement =
    counts.inline > 0 && counts.divided === 0 ? 'inline' : 'divided';
  return {
    hasContent: counts.divided + counts.inline > 0,
    placement,
    value,
  };
}

interface MenuContentSlotProps {
  value: MenuContentSlotValue;
  /** Caps inline content at `--cds-navbar-menu-content-width`. */
  capsInline?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Hosts authored menu content. Content for other menus renders nothing, so
 * the slot collapses with `empty:hidden` when no content matches.
 */
export function MenuContentSlot({
  value,
  capsInline = false,
  className,
  children,
}: MenuContentSlotProps) {
  const slotValue = useMemo(
    () => (capsInline ? { ...value, capsInline } : value),
    [value, capsInline],
  );
  return (
    <MenuContentSlotContext.Provider value={slotValue}>
      <div
        data-subnav-menu-content=''
        className={cn(className, 'empty:hidden')}
      >
        {children}
      </div>
    </MenuContentSlotContext.Provider>
  );
}
